import React, { useState } from 'react';
// import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { PlusPlansFeatures, createSubscriptionPriceGetter, showAwnFreeTrial, subIsTeams } from './';
import { useDoModal } from '../user/redux/doModal';
import './PlusSubscriptionSelector.less';

function PlusSubscriptionSelector({
  annual: annualProp,
  onAnnualChange,
  onUpgrade,
  user,
  device,
  showGiftCodeLink = true,
  onGiftCodeClick,
  showUpgradePreview = false,
  showDowngradePreview = false
}) {
  const history = useHistory();
  const { doModal } = useDoModal();

  // Detect current subscription state for setting initial billing period
  const currentIsTeams = user?.plus && subIsTeams(user.plus);
  const currentIsMonthly = user?.plus && /monthly/.test(user.plus.productName);

  // If showing preview, initialize annual state based on current subscription
  const initialAnnual = (showUpgradePreview || showDowngradePreview) && annualProp === undefined
    ? !currentIsMonthly
    : (annualProp !== undefined ? annualProp : true);

  const [internalAnnual, setInternalAnnual] = useState(initialAnnual);

  // Use prop if provided, otherwise use internal state
  const annual = annualProp !== undefined ? annualProp : internalAnnual;

  const freeTrialAvail = showAwnFreeTrial(user);
  const active = false;
  const btnLabel = freeTrialAvail ? 'Try 1 month free' : 'Upgrade';

  // Helper function to get the right price to display
  const getSubscriptionPriceDisplay = createSubscriptionPriceGetter(device);

  // Handle annual/monthly toggle
  const handleAnnualChange = (value) => {
    if (onAnnualChange) {
      onAnnualChange(value);
    } else {
      setInternalAnnual(value);
    }
  };

  // Handle upgrade button click
  const handleUpgrade = (tier) => {
    if (onUpgrade) {
      // Use provided callback (for controlled mode, like in PlusSubscriptionModal)
      onUpgrade(tier);
    } else {
      // Handle navigation internally (for standalone mode, like in SubscribePage)
      const period = annual ? 'yearly' : 'monthly';
      const productBase = tier === 'teams' ? 'awn-plus-for-teams' : 'awn-plus-account';
      const subscription = `${productBase}-${period}`;

      // Add plusUserSwitch flag if this is an upgrade/downgrade
      const isPlusUserSwitch = showUpgradePreview || showDowngradePreview || !!user?.plus;
      const url = isPlusUserSwitch
        ? `/subscriptions/checkout?subscription=${subscription}&plusUserSwitch=true`
        : `/subscriptions/checkout?subscription=${subscription}`;

      doModal(null); // Close any open modal before navigating
      history.push(url);
    }
  };

  // Handle gift code click
  const handleGiftCodeClick = (tier) => {
    if (onGiftCodeClick) {
      // Use provided callback (for controlled mode)
      onGiftCodeClick(tier);
    } else {
      // Open gift code modal - it will redirect to checkout after validation
      doModal({ type: 'gift-code' });
    }
  };

  // Reusable annual/monthly toggle component
  const toggleComponent = (
    <div className={classNames('awn-switch two-way switch-wrap global-toggle', annual ? 'annual' : 'monthly')}>
      <div className={classNames('switch-container', annual ? 'annual' : 'monthly')}>
        <span className='slider' />
        <button
          className={classNames('switch-btn', { active: annual })}
          onClick={() => handleAnnualChange(true)}
        >
          Annual
        </button>
        <button
          className={classNames('switch-btn', { active: !annual })}
          onClick={() => handleAnnualChange(false)}
        >
          Monthly
        </button>
      </div>
      <span className='discount'>Save 17%</span>
    </div>
  );

  // Show upgrade preview (Individual -> Teams)
  if (showUpgradePreview) {
    return (
      <div className="payment-plus-subscription-selector">
        {toggleComponent}
        <div className='product-tiers preview'>
          <div className={classNames('tier preview-tier tier-2', { annual })}>
            <div className='top'>
              <h3>AWN+ Teams</h3>
              <h2>
                {(() => {
                  const pricing = getSubscriptionPriceDisplay('teams', annual, false);
                  return <>{pricing.monthlyPrice}<span className='per-month'>/month</span></>;
                })()}
              </h2>
              {annual && (() => {
                const pricing = getSubscriptionPriceDisplay('teams', annual, false);
                return <p className='billed-annually'>{pricing.billedText}</p>;
              })()}
              <button
                className='btn btn-primary'
                onClick={() => handleUpgrade('teams')}
              >
                Upgrade to Teams
              </button>
            </div>
            <div className='bottom'>
              <div className='group'>
                <div className='line'>
                  <div className='val plus'>
                    <strong>Weather Forecast: </strong>
                    15 Days
                  </div>
                  <div className='val plus'>
                    <strong>Data History: </strong>
                    5 years
                  </div>
                </div>
                <div className='line'>
                  <div className='label'>New Features</div>
                  <div className='val plus'>
                    <strong>Team Alerts: </strong>
                    Organize weather alerts for a team. As an admin, you can create custom alert sets and assign them to members, who will receive alerts via email, SMS, or in-app.
                  </div>
                  <div className='val plus'>
                    <strong>Map Filtering: </strong>
                    Customize the stations shown on the map. Select from your own stations, favorited ones, and NOAA sources
                  </div>
                </div>
                <div className='line'>
                  <strong>Your remaining subscription time will not be lost.</strong><br/><br/>
                  <p>
                    We'll prorate the change by calculating the unused portion of your current subscription and applying it as a credit to your account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show downgrade preview (Teams -> Individual)
  if (showDowngradePreview) {
    return (
      <div className="payment-plus-subscription-selector">
        {toggleComponent}
        <div className='product-tiers preview'>
          <div className={classNames('tier preview-tier tier-1', { annual })}>
            <div className='top'>
              <h3>AWN+ Individual</h3>
              <h2>
                {(() => {
                  const pricing = getSubscriptionPriceDisplay('plus', annual, false);
                  return <>{pricing.monthlyPrice}<span className='per-month'>/month</span></>;
                })()}
              </h2>
              {annual && (() => {
                const pricing = getSubscriptionPriceDisplay('plus', annual, false);
                return <p className='billed-annually'>{pricing.billedText}</p>;
              })()}
              <button
                className='btn btn-primary'
                onClick={() => handleUpgrade('plus')}
              >
                Downgrade to Individual
              </button>
            </div>
            <div className='bottom'>
              <div className='group'>
                <div className='line'>
                  <div className='val minus'>
                    <strong>Weather Forecast: </strong>
                    Reduced to 10 Days
                  </div>
                  <div className='val minus'>
                    <strong>Data History: </strong>
                    Reduced to 3 years
                  </div>
                </div>
                <div className='line'>
                  <div className='label'>You will also lose access to the following features:</div>
                  <div className='val minus'>
                    <strong>Team Alerts: </strong>
                    Team alert management and member assignment will no longer be available.
                  </div>
                  <div className='val minus'>
                    <strong>Advanced Map Filtering: </strong>
                    Advanced filtering options for map stations will be removed.
                  </div>
                </div>
                <div className='line'>
                  <strong>Your remaining subscription time will not be lost.</strong><br/><br/>
                  <p>
                    We'll prorate the change by calculating the unused portion of your current subscription and applying it as a credit to your account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Returns { label, disabled } for each tier button
  const getButtonState = (tier) => {
    if (!user?.plus) return { label: btnLabel, disabled: false }

    const isCurrentTier = (tier === 'teams') === currentIsTeams
    const periodMatches = annual !== currentIsMonthly

    if (isCurrentTier && periodMatches) {
      return { label: 'Current', disabled: true }
    }
    if (isCurrentTier && !periodMatches) {
      return { label: annual ? 'Switch to Annual' : 'Switch to Monthly', disabled: false }
    }
    return { label: currentIsTeams ? 'Downgrade to Individual' : 'Upgrade to Teams', disabled: false }
  }

  // Default view: Show full plan selector
  return (
    <div className="payment-plus-subscription-selector">
      {toggleComponent}
      <div className='product-tiers'>
        {(() => {
          const { label: tier1Label, disabled: tier1Disabled } = getButtonState('plus')
          return (
          <div className={classNames('tier tier-1', {
            active: user?.plus && !currentIsTeams,
            annual,
            trial: freeTrialAvail && !user?.plus
          })}>
          <div className='top'>
            <h3>AWN+ Individual</h3>
            <h2>
              {(() => {
                const pricing = getSubscriptionPriceDisplay('plus', annual, false);
                return <>{pricing.monthlyPrice}<span className='per-month'>/month</span></>;
              })()}
            </h2>
            {annual && (() => {
              const pricing = getSubscriptionPriceDisplay('plus', annual, false);
              return <p className='billed-annually'>{pricing.billedText}</p>;
            })()}
            <button
              className='btn btn-primary'
              onClick={() => handleUpgrade('plus')}
              disabled={tier1Disabled}
            >{tier1Label}</button>
            {showGiftCodeLink && (
              <p>
                <a onClick={() => handleGiftCodeClick('plus')}>Enter Gift code</a>
              </p>
            )}
            {/* {freeTrialAvail && <p className='trial'>Limited Time Offer - First Month Free!</p>} */}
          </div>
          <div className='bottom'>
            Best for individuals who need deeper insights, longer forecasts, and more advanced data tools.
          </div>
        </div>
          )
        })()}
        {(() => {
          const { label: tier2Label, disabled: tier2Disabled } = getButtonState('teams')
          return (
          <div className={classNames('tier tier-2', { active: user?.plus && currentIsTeams, annual })}>
          <div className='top'>
            <h3>AWN+ Teams</h3>
            <h2>
              {(() => {
                const pricing = getSubscriptionPriceDisplay('teams', annual, false);
                return <>{pricing.monthlyPrice}<span className='per-month'>/month</span></>;
              })()}
            </h2>
            {annual && (() => {
              const pricing = getSubscriptionPriceDisplay('teams', annual, false);
              return <p className='billed-annually'>{pricing.billedText}</p>;
            })()}
            <button
              className='btn btn-primary'
              onClick={() => handleUpgrade('teams')}
              disabled={tier2Disabled}
            >{tier2Label}</button>
            {showGiftCodeLink && (
              <p>
                <a onClick={() => handleGiftCodeClick('teams')}>Enter Gift code</a>
              </p>
            )}
          </div>
          <div className='bottom'>
            Ideal for teams and small businesses who need advanced tools, team management, and extended data history.
          </div>
        </div>
          )
        })()}
      </div>
      <div className='features'>
        <h3>AWN+ Plan Features</h3>
        <PlusPlansFeatures
          onClick={handleUpgrade}
          user={user}
        />
      </div>
    </div>
  );
}

PlusSubscriptionSelector.propTypes = {};
PlusSubscriptionSelector.defaultProps = {};
PlusSubscriptionSelector.displayName = 'PlusSubscriptionSelector';

export default PlusSubscriptionSelector;
